import {
	BillingInfo,
	DataUsage,
	DeviceDto,
	DeviceList,
	PublicRecordResponse,
	SubscriptionRequest,
	SubscriptionResponse,
	UserInfoDto,
} from "@somewear/api";
import { Configuration, TraceIdGenerator } from "@somewear/model";
import type { Store } from "redux";
import type { Observable } from "rxjs";
import { firstValueFrom, from, of } from "rxjs";
import { catchError, map, mergeMap, take } from "rxjs/operators";

import { AuthController } from "./authController";
import { selectActiveOrganizationId, selectActiveUserAccountId } from "./store";

const OK = 200;

interface IProto {
	deserializeBinary(bytes: Uint8Array): any;
}

interface IProtoInstance {
	serializeBinary(): Uint8Array;
}

export class RestClient {
	private static instance: RestClient;
	private store: Store;

	static init(store: Store) {
		this.instance = new RestClient(store);
	}

	private constructor(store: Store) {
		this.store = store;
	}

	static getInstance(): RestClient {
		if (!this.instance) throw Error("RestClient has not been initialized");
		return this.instance;
	}

	private static fullUrl(api: string): string {
		const baseUrl =
			Configuration.config.somewear.baseUrl !== "auto"
				? Configuration.config.somewear.baseUrl
				: window.location.origin + "/";
		return baseUrl + api;
	}

	/*private static parseResponse(response: Response, resource: IProto): Promise<any> {
		return new Promise((resolve) => {
			response.arrayBuffer().then((arrayBuf) => {
				let proto = resource.deserializeBinary(new Uint8Array(arrayBuf));
				resolve(proto);
			});
		});
	}*/

	private getResource<T>(apiUrl: string, resource?: IProto): Observable<T> {
		return AuthController.tokenString$().pipe(
			mergeMap((tokenString) => {
				const headers: any = {
					Authorization: tokenString,
					"Content-Type": "application/x-protobuf; charset=UTF-8",
					"x-api-key": Configuration.config.somewear.apiKey,
					"x-platform": "Web",
					"x-b3-traceid": TraceIdGenerator.instance.generateTraceId(),
					"x-b3-spanid": TraceIdGenerator.instance.generateSpanId(),
				};

				const state = this.store.getState();
				const userAccountId = selectActiveUserAccountId(state);
				const organizationId = selectActiveOrganizationId(state);
				// let userAccountId = UserSource.getInstance().activeUserAccountId;
				if (userAccountId != null) {
					headers["x-user-account-id"] = userAccountId;
				}

				if (organizationId !== undefined) {
					headers["x-organization-id"] = organizationId;
				}

				const fetchPromise = fetch(RestClient.fullUrl(apiUrl), {
					mode: Configuration.config.somewear.cors ? "cors" : "same-origin",
					headers: headers,
				});

				return from(fetchPromise);
			}),
			mergeMap((response) => {
				if (response.status === OK) {
					if (resource === undefined) {
						return of(response);
					} else {
						return from(response.arrayBuffer()).pipe(
							map((payload) => {
								return resource.deserializeBinary(new Uint8Array(payload));
							}),
						);
					}
				} else {
					throw new Error(`getResource: Failed to get ${apiUrl}`);
				}
			}),
			catchError((error) => {
				console.error(error);
				throw error;
			}),
			take(1),
		);
	}

	private setResource(
		method: string,
		apiUrl: string,
		resource: IProtoInstance,
	): Observable<Response> {
		return AuthController.tokenString$().pipe(
			mergeMap((tokenString) => {
				const headers: any = {
					Authorization: tokenString,
					"Content-Type": "application/x-protobuf; charset=UTF-8",
					"x-api-key": Configuration.config.somewear.apiKey,
					"x-platform": "Web",
					"x-b3-traceid": TraceIdGenerator.instance.generateTraceId(),
					"x-b3-spanid": TraceIdGenerator.instance.generateSpanId(),
				};

				const state = this.store.getState();
				const userAccountId = selectActiveUserAccountId(state);
				const organizationId = selectActiveOrganizationId(state);
				// let userAccountId = UserSource.getInstance().activeUserAccountId;
				if (userAccountId != null) {
					headers["x-user-account-id"] = userAccountId;
				}

				if (organizationId !== undefined) {
					headers["x-organization-id"] = organizationId;
				}

				const fetchPromise = fetch(RestClient.fullUrl(apiUrl), {
					mode: Configuration.config.somewear.cors ? "cors" : "same-origin",
					headers: headers,
					method: method,
					body: new Uint8Array(resource.serializeBinary()),
				});

				return from(fetchPromise);
			}),
			mergeMap((response) => {
				if (response.status === OK) {
					return of(response);
				} else {
					const errorMessage = `putResource: Failed to set ${apiUrl};`;
					console.log(response);
					console.error();
					throw errorMessage;
				}
			}),
			catchError((error) => {
				console.error(error);
				throw error;
			}),
			take(1),
		);
	}

	private putResource(apiUrl: string, resource: IProtoInstance) {
		return firstValueFrom(this.setResource("PUT", apiUrl, resource));
	}

	private postResource(apiUrl: string, resource: IProtoInstance) {
		return firstValueFrom(this.setResource("POST", apiUrl, resource));
	}

	getChangelogArtifact(pageId: string, artifactName: string) {
		return firstValueFrom(
			this.getResource<Response>(
				Configuration.config.somewear.api.changelogArtifact +
					"/" +
					pageId +
					"/" +
					artifactName,
			),
		);
	}

	getUserInfo() {
		return firstValueFrom(
			this.getResource<UserInfoDto>(Configuration.config.somewear.api.userInfo, UserInfoDto),
		);
	}

	getUuid() {
		return firstValueFrom(
			this.getResource<PublicRecordResponse>(
				Configuration.config.somewear.api.uuid,
				PublicRecordResponse,
			),
		);
	}

	getApp(android: boolean) {
		return firstValueFrom(
			this.getResource<null>(
				android
					? Configuration.config.somewear.api.appAndroid
					: Configuration.config.somewear.api.appiOS,
			),
		);
	}

	getDevice() {
		return firstValueFrom(
			this.getResource<DeviceDto>(Configuration.config.somewear.api.device, DeviceDto),
		);
	}

	getBillingInfo() {
		return firstValueFrom(
			this.getResource<BillingInfo>(
				Configuration.config.somewear.api.billingInfo,
				BillingInfo,
			),
		);
	}

	getOrderDevices() {
		return firstValueFrom<DeviceList>(
			this.getResource(Configuration.config.somewear.api.orderDevices, DeviceList),
		);
	}

	getSubscriptionsWithDevice() {
		return firstValueFrom(
			this.getResource<SubscriptionResponse>(
				Configuration.config.somewear.api.subscriptionWithDevice,
				SubscriptionResponse,
			),
		);
	}

	getSubscriptionDetail(subscriptionCode: string) {
		return firstValueFrom(
			this.getResource<SubscriptionRequest>(
				Configuration.config.somewear.api.subscriptions + "/" + subscriptionCode,
				SubscriptionRequest,
			),
		);
	}

	registerSubscription(subscriptionRequest: SubscriptionRequest) {
		return new Promise<SubscriptionRequest>((resolve, reject) => {
			this.putResource(
				Configuration.config.somewear.api.subscriptions + "/register",
				subscriptionRequest,
			)
				.then((response) => {
					resolve(subscriptionRequest);
				})
				.catch((error) => {
					reject(error);
				});
		});
	}

	updateUserInfo(userInfo: UserInfoDto) {
		return new Promise((resolve, reject) => {
			this.putResource(Configuration.config.somewear.api.userInfo, userInfo)
				.then((response) => {
					resolve(userInfo);
				})
				.catch((error) => {
					reject(error);
				});
		});
	}

	updateSosInfo(userInfo: UserInfoDto) {
		return new Promise((resolve, reject) => {
			this.putResource(Configuration.config.somewear.api.sosRegister, userInfo)
				.then((response) => {
					resolve(userInfo);
				})
				.catch((error) => {
					reject(error);
				});
		});
	}

	updateUser(userInfo: UserInfoDto): Promise<UserInfoDto> {
		return new Promise((resolve, reject) => {
			this.putResource(Configuration.config.somewear.api.user, userInfo)
				.then((response) => {
					response
						.arrayBuffer()
						.then((arrayBuf) => {
							try {
								const userInfoResponse = UserInfoDto.deserializeBinary(
									new Uint8Array(arrayBuf),
								);
								resolve(userInfoResponse);
							} catch (error) {
								console.error("updateUser: Failed to parse UserInfoDto");
								reject(Error("updateUser: Failed to parse UserInfoDto"));
							}
						})
						.catch((error) => {
							console.error(error);
							reject(Error(`updateUser: Failed to get bytes error`));
						});
				})
				.catch((error) => {
					reject(error);
				});
		});
	}

	updateSubscription(subscription: SubscriptionRequest): Promise<SubscriptionRequest> {
		return new Promise((resolve, reject) => {
			this.putResource(Configuration.config.somewear.api.subscriptions, subscription)
				.then((response) => {
					response
						.arrayBuffer()
						.then((arrayBuf) => {
							try {
								const subscriptionResponse = SubscriptionRequest.deserializeBinary(
									new Uint8Array(arrayBuf),
								);
								resolve(subscriptionResponse);
							} catch (error) {
								console.error(
									"updateSubscription: Failed to parse SubscriptionRequest",
								);
								reject(
									Error(
										"updateSubscription: Failed to parse SubscriptionRequest",
									),
								);
							}
						})
						.catch((error) => {
							console.error(error);
							reject(Error(`updateSubscription: Failed to get bytes`));
						});
				})
				.catch((error) => {
					reject(error);
				});
		});
	}

	addSubscription(subscription: SubscriptionRequest): Promise<SubscriptionRequest> {
		return new Promise((resolve, reject) => {
			this.postResource(Configuration.config.somewear.api.subscriptions, subscription)
				.then((response) => {
					response
						.arrayBuffer()
						.then((arrayBuf) => {
							try {
								const subscriptionResponse = SubscriptionRequest.deserializeBinary(
									new Uint8Array(arrayBuf),
								);
								resolve(subscriptionResponse);
							} catch (error) {
								console.error(
									"addSubscription: Failed to parse SubscriptionRequest",
								);
								reject(
									Error("addSubscription: Failed to parse SubscriptionRequest"),
								);
							}
						})
						.catch((error) => {
							console.error("addSubscription: Failed to get bytes");
							console.error(error);
							reject(error);
						});
				})
				.catch((error) => {
					reject(error);
				});
		});
	}

	activateDevices(subscriptions: SubscriptionResponse): Promise<SubscriptionResponse> {
		return new Promise((resolve, reject) => {
			this.postResource(Configuration.config.somewear.api.activateDevices, subscriptions)
				.then((response) => {
					response
						.arrayBuffer()
						.then((arrayBuf) => {
							try {
								const subscriptionResponse = SubscriptionResponse.deserializeBinary(
									new Uint8Array(arrayBuf),
								);
								resolve(subscriptionResponse);
							} catch (error) {
								console.error(
									"activateDevices: Failed to parse subscriptionResponse",
								);
								reject(error);
							}
						})
						.catch((error) => {
							console.error("activateDevices: Fialed to get bytes");
							reject(error);
						});
				})
				.catch((error) => {
					console.error(error);
					reject(error);
				});
		});
	}

	getDataUsage(subscriptionCode: string) {
		return firstValueFrom(
			this.getResource<DataUsage>(
				Configuration.config.somewear.api.dataUsage + "/" + subscriptionCode,
				DataUsage,
			),
		);
	}

	getBeamDownload() {
		return firstValueFrom(this.getResource<Response>("api/downloads/beam")).then((response) =>
			response.json(),
		);
	}
}
